<template>
    <v-card>
        <v-card-title>
            <template v-if="isProcessReport">
                {{ taskName }} - {{ report.processName }}
            </template>
            <template v-else>
                {{ taskName }} {{ report.objectNumber }}
                <template v-if="hasMultiplePlates && report.plateNumber">
                    - {{ plateVerboseName }} {{ report.plateNumber }}
                </template>
            </template>
        </v-card-title>
        <v-divider />
        <v-card-text>
            <v-container class="pa-0">
                <v-row v-if="report.makeReadyStart">
                    <v-col cols="12" sm="12" md="6" lg="4" class="d-flex">
                        <v-icon class="mr-2" color="success">
                            mdi-timer-outline
                        </v-icon>
                        <span class="text-subtitle-1">
                            <strong>Rozpoczęcie przyrządu:</strong>
                            {{ formatDate(report.makeReadyStart) }}
                        </span>
                    </v-col>
                    <v-col cols="12" sm="12" md="6" lg="4" class="d-flex">
                        <v-icon class="mr-2" color="error">
                            mdi-timer-off-outline
                        </v-icon>
                        <span class="text-subtitle-1">
                            <strong>Zakończenie przyrządu:</strong>
                            {{ formatDate(report.makeReadyFinish) }}
                        </span>
                    </v-col>
                    <v-col cols="12" sm="12" md="6" lg="4" class="d-flex">
                        <v-icon class="mr-2" color="info">
                            mdi-clock-outline
                        </v-icon>
                        <span class="text-subtitle-1" :class="{'error--text': isOperatorLate || report.makeReadyDelayed}">
                            <strong>Czas przyrządu:</strong>
                            <template v-if="isReportFinished || isReportStopped">
                                {{ parseDuration(report.makeReadyDuration) }}
                            </template>
                            <template v-else-if="report.makeReadyDuration">
                                {{ parseDuration(report.makeReadyDuration) }}
                            </template>
                            <template v-else>
                                <template v-if="delay">
                                    {{ parseDuration(delay) }}
                                </template>
                                <template v-else>
                                    {{ parseDuration(duration) }}
                                </template>
                            </template>
                        </span>
                    </v-col>
                </v-row>
                <v-divider v-if="report.makeReadyFinish" />
                <v-row v-if="!report.makeReadyStart || report.makeReadyFinish || isReportFinished">
                    <v-col cols="12" sm="12" md="6" lg="4" class="d-flex">
                        <v-icon class="mr-2" color="success">
                            mdi-timer-outline
                        </v-icon>
                        <span class="text-subtitle-1">
                            <template v-if="[1, 2].includes(report.objectType)">
                                <strong>Rozpoczęcie jazdy:</strong>
                            </template>
                            <template v-else>
                                <strong>Rozpoczęcie:</strong>
                            </template>
                            {{ formatDate(report.start) }}
                        </span>
                    </v-col>
                    <v-col cols="12" sm="12" md="6" lg="4" class="d-flex">
                        <v-icon class="mr-2" color="error">
                            mdi-timer-off-outline
                        </v-icon>
                        <span class="text-subtitle-1">
                            <template v-if="[1, 2].includes(report.objectType)">
                                <strong>Zakończenie jazdy:</strong>
                            </template>
                            <template v-else>
                                <strong>Zakończenie:</strong>
                            </template>
                            {{ formatDate(report.finish) }}
                        </span>
                    </v-col>
                    <v-col cols="12" sm="12" md="6" lg="4" class="d-flex">
                        <v-icon class="mr-2" color="info">
                            mdi-clock-outline
                        </v-icon>
                        <span class="text-subtitle-1" :class="{'error--text': isOperatorLate}">
                            <template v-if="[1, 2].includes(report.objectType) && !isReportFinished">
                                <strong>Czas:</strong>
                            </template>
                            <template v-else-if="[1, 2].includes(report.objectType)">
                                <strong>Czas jazdy:</strong>
                            </template>
                            <template v-else>
                                <strong>Czas:</strong>
                            </template>
                            <template v-if="isReportFinished || isReportStopped">
                                {{ parseDuration(report.productionDuration) }}
                            </template>
                            <template v-else>
                                <template v-if="delay">
                                    {{ parseDuration(delay) }}
                                </template>
                                <template v-else-if="timeWithoutLimit">
                                    {{ parseDuration(timeWithoutLimit) }}
                                </template>
                                <template v-else>
                                    {{ parseDuration(duration) }}
                                </template>
                            </template>
                            <span v-if="isReportFinished && report.remainingTime < 0" class="text-subtitle-1 ml-1">
                                <strong>Opóźnienie:</strong>
                                <template>
                                    {{ parseDuration(Math.abs(report.remainingTime)) }}
                                </template>
                            </span>
                        </span>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="additionalActionsReportProcessCodeProductionSite.length > 0" cols="12">
                        <v-card>
                            <v-card-text>
                                <v-data-table
                                    :headers="additionalActionsReportProcessCodeProductionSiteHeaders"
                                    :items="additionalActionsReportProcessCodeProductionSite"
                                    disable-pagination
                                    hide-default-footer
                                    :mobile-breakpoint="0"
                                    show-select
                                    name="additionalActionsReportProcessCodeProductionSiteData"
                                >
                                    <template #header.data-table-select>
                                        <v-simple-checkbox v-if="!isReportFinished" v-model="selectAllReportProcessCode" :ripple="false" @input="toggleSelectAllReportProcessCode($event)" />
                                    </template>
                                    <template #item.data-table-select="{item}">
                                        <v-simple-checkbox v-model="item.isSelected" :disabled="isReportFinished" :ripple="false" @input="updateSelectAllReportProcessCode()" />
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>
    </v-card>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'

export default {
    name: 'ReportTimerTracker',
    mixins:[ parse, dateFormatter ],
    props: {
        report: {
            type: Object,
            required: true
        },
        taskName: {
            type: String,
            default: ''
        },
        delay: {
            type: Number,
            default: 0
        },
        duration: {
            type: Number,
            default: 0
        },
        timeWithoutLimit: {
            type: Number,
            default: 0
        },
        additionalActionsReportProcessCodeProductionSite: {
            type: Array,
            default: () => []
        },
    },
    computed: {
        isOperatorLate() {
            return this.report.remainingTime !== null && this.delay && this.report.statusId === 1
        },
        isReportFinished() {
            return this.report?.statusId == 99
        },
        isReportStopped() {
            return this.report.statusId == 90
        },
        isProcessReport() {
            return this.report.objectType === 3
        },
        hasMultiplePlates() {
            return this.report.objectDetail.totalPlates > 1
        },
        plateVerboseName() {
            return this.report.objectDetail.reportItemType === 60 ? 'Składka' : 'Montaż'
        },
        additionalActionsReportProcessCodeProductionSiteHeaders() {
            return [
                {
                    text: 'Wykonane czynności',
                    value: 'name',
                    sortable: false
                }
            ]
        },
    },
    methods: {
        toggleSelectAllReportProcessCode(event) {
            this.$emit('toggle-select-all-report-process-code', event)
        },
        updateSelectAllReportProcessCode() {
            this.$emit('update-select-all-report-process-code')
        }
    }
};
</script>